import React, { useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import ListItemText from '@material-ui/core/ListItemText';

import { IconSvg } from 'components/atoms';

import NestedMenuItemMobile from './NestedMenuItemMobile';

const useStyles = makeStyles((theme) => ({
  root: {
    // padding: theme.spacing(0.5, 1.375),
    flexWrap: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  select: {
    color: 'black',
    '&:hover': {
      borderBottom: 'none !important',
    },
    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: 'none !important',
    },
  },
  selectRoot: {
    padding: theme.spacing(1),
    fontFamily: 'Roboto',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    boxShadow: '0px 3px 6px #00000029',
    borderRadius: '999px',
    width: '250px',
    '&:focus': {
      borderRadius: '999px',
    },
  },
  selectOutlined: {
    border: 'black',
    '&:hover': {
      border: 'none',
    },
  },
  icon: {
    margin: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  selectIcon: {
    color: 'black',
  },
  selectPresent: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '999px',
  },
  selectListItemText: {
    whiteSpace: 'normal',
    marginRight: '6px',
    fontWeight: '500',
    fontSize: '14px',
    '& > span': {
      lineHeight: 1.2,
    },
  },
}));

const FilterSelectCascade = (props) => {
  const { className, items, placeholder, startText, name, icon, iconProps, onChange, value, ...others } = props;

  const classes = useStyles();
  const [selected, setSelected] = React.useState(value);

  const handleChange = (event) => {
    setSelected(event.target.value);
    if (onChange) onChange(event);
  };

  const [menuPosition, setMenuPosition] = React.useState(null);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const handleRightClick = (event) => {
    if (menuPosition) {
      return;
    }
    event.preventDefault();
    setMenuPosition({
      top: event.pageY,
      left: event.pageX,
    });
  };

  const handleItemClick = (event) => {
    handleChange(event);
    setMenuPosition(null);
  };

  const transformOriginHorizontal = 60;

  const MenuItemRecursive = React.forwardRef(({ items, indexRoot = 0 }, ref) => (
    <>
      {items?.map((item, index) => {
        if (item.children && item.children.length)
          /* isSm ? ( */
          return (
            <NestedMenuItemMobile
              key={item.value}
              label={item.description}
              parentMenuOpen={!!menuPosition}
              transformOriginHorizontal={transformOriginHorizontal}
              onClick={(e) => {
                handleItemClick({
                  ...e,
                  target: { ...e.target, value: item.value, name: item.description },
                });
              }}
              tabIndex={`${indexRoot}_${index}`}
              tabIndexProp={`${indexRoot}_${index}`}
            >
              <MenuItemRecursive items={item.children} indexRoot={`${indexRoot}_${index}`} />
            </NestedMenuItemMobile>
          );
        // ) : (
        //   <NestedMenuItem
        //     key={item.value}
        //     label={item.description}
        //     parentMenuOpen={!!menuPosition}
        //     transformOriginHorizontal={transformOriginHorizontal}
        //     onClick={(e) => {
        //       console.log('NestedMenuItemClick', e);
        //       handleItemClick({
        //         ...e,
        //         target: { ...e.target, value: item.value, name: item.description },
        //       });
        //     }}
        //   >
        //     <MenuItemRecursive items={item.children} />
        //   </NestedMenuItem>
        // );
        else
          return (
            <MenuItem
              key={item.value}
              value={item.value}
              onClick={(e) =>
                handleItemClick({
                  ...e,
                  target: { ...e.target, value: item.value, name: item.description },
                })
              }
            >
              <span>
                <ListItemText className={classes.selectListItemText} primary={item.description} />
              </span>
            </MenuItem>
          );
      })}
    </>
  ));

  const hasValueSelected = () => {
    return Array.isArray(selected) ? selected.length : selected;
  };

  return (
    <Grid
      className={clsx(classes.root, className, {
        [classes.selectPresent]: hasValueSelected(),
      })}
      container
      spacing={0}
      alignItems='flex-start'
    >
      <Grid item>
        <Select
          {...others}
          open={Boolean(menuPosition)}
          name={name}
          displayEmpty
          variant='standard'
          disableUnderline
          className={classes.select}
          onOpen={handleRightClick}
          MenuProps={{
            open: !!menuPosition,
            onClose: () => setMenuPosition(null),
            // onSelect: () => setMenuPosition(null),
            anchorPosition: menuPosition,
          }}
          value={selected}
          // onChange={handleChange}
          renderValue={() => {
            if (selected?.length === 0 || !selected) {
              return (
                <Grid container>
                  <Grid item xs={1} style={{ margin: '0 auto' }}>
                    <IconSvg name={icon} viewBox='0 0 21.397 17.342' className={classes.icon} {...iconProps} />
                  </Grid>
                  <Grid item xs={10} style={{ margin: 'auto 6px', fontWeight: '500', fontSize: '14px' }}>
                    {placeholder}
                  </Grid>
                </Grid>
              );
            }

            const isSelectedValue = (selected, currentValue) => {
              if (Array.isArray(selected)) return selected.includes(currentValue.value);

              return currentValue.value === selected;
            };

            const getSelectedItems = (arrayItems) => {
              return arrayItems.reduce((acc, curr) => {
                return [
                  ...acc,
                  ...(curr.children && !isSelectedValue(selected, curr)
                    ? getSelectedItems(curr.children)
                    : isSelectedValue(selected, curr)
                    ? [curr]
                    : ['']),
                ].filter((x) => x);
              }, []);
            };

            const selectedItems = getSelectedItems(items);

            return (
              <Grid container>
                <Grid xs={1}>
                  <IconSvg name={icon} className={classes.icon} {...iconProps} />
                </Grid>
                <Grid xs={10} style={{ margin: 'auto 6px', fontWeight: '500', fontSize: '14px' }}>
                  {(startText || '') + selectedItems.map((i) => i.description).join(', ')}
                </Grid>
              </Grid>
            );
          }}
          classes={{
            root: classes.selectRoot,
            icon: classes.selectIcon,
          }}
        >
          <MenuItem disabled value=''>
            <em> {placeholder}</em>
          </MenuItem>

          <MenuItemRecursive items={items} />
        </Select>
      </Grid>
    </Grid>
  );
};

const FilterSelectItemShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  description: PropTypes.string.isRequired,
});

FilterSelectItemShape.children = PropTypes.arrayOf(FilterSelectItemShape);

FilterSelectCascade.propTypes = {
  ...Select.propTypes,
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Placeholder
   */
  placeholder: PropTypes.string,
  /**
   * Text to show before the selected value on input
   */
  startText: PropTypes.string,
  /**
   * Item source data as shaped value | description
   */
  items: PropTypes.arrayOf(FilterSelectItemShape).isRequired,
};

export default FilterSelectCascade;
